/**
 * Unity 메시지 전송 모듈
 */

import { unityMessageQueue, type QueuedMessage } from "./unityMessageQueue";
import { isUnityReady, getUnityInstance } from "./unityReadyCheck";
import { refreshEditorImportMessage } from "../../../../services/editor/editorImportProjection";

const isDev = process.env.NODE_ENV === "development";

/**
 * Unity로 메시지 전송 (안전한 버전 - 준비되지 않으면 큐에 추가)
 */
export function sendToUnitySafe(
  gameObject: string,
  method: string,
  message: string
): void {
  const iframe = getUnityInstance();

  if (!isUnityReady() || !iframe) {
    if (isDev) console.warn("⚠️ Unity not ready, queuing message");
    unityMessageQueue.enqueue([gameObject, method, message]);
    return;
  }

  try {
    // Unity WebGL iframe 환경에서는 postMessage를 통해 통신
    const messageData = {
      type: "REACT_TO_UNITY", // Unity index.html에서 처리하는 타입
      gameObject,
      method,
      message: refreshEditorImportMessage(gameObject, method, String(message)),
    };

    // contentWindow로 postMessage 전송
    if (iframe.contentWindow) {
      iframe.contentWindow.postMessage(messageData, "*");
      if (isDev) console.log("📤 Unity로 메시지 전송 (postMessage):", {
        gameObject,
        method,
        messagePreview: String(message).substring(0, 50),
      });
      return;
    }

    throw new Error("iframe contentWindow를 찾을 수 없음");
  } catch (err) {
    console.error("❌ postMessage 실패:", err);
    unityMessageQueue.enqueue([gameObject, method, message]);
  }
}

/**
 * Unity로 원시 메시지 전송 (큐 없이 직접 전송)
 */
export function sendToUnityRaw(
  gameObject: string,
  method: string,
  value: string
): boolean {
  const iframe = getUnityInstance();
  if (iframe?.contentWindow) {
    try {
      iframe.contentWindow.postMessage(
        {
          type: "reactToUnity_command",
          gameObject,
          method,
          message: refreshEditorImportMessage(gameObject, method, value),
        },
        "*"
      );
      return true;
    } catch (error) {
      console.error("❌ Failed to send message to Unity:", error);
      return false;
    }
  }
  return false;
}

/**
 * 큐에 있는 메시지 처리
 */
export function processMessageQueue(): void {
  if (unityMessageQueue.isProcessing() || unityMessageQueue.isEmpty()) {
    return;
  }

  if (!isUnityReady()) {
    return;
  }

  unityMessageQueue.setProcessing(true);

  const queueCopy: QueuedMessage[] = [];
  while (!unityMessageQueue.isEmpty()) {
    const message = unityMessageQueue.dequeue();
    if (message) {
      queueCopy.push(message);
    }
  }

  const iframe = getUnityInstance();
  if (!iframe) {
    // iframe이 없으면 메시지를 다시 큐에 추가
    queueCopy.forEach((msg) => unityMessageQueue.enqueue(msg));
    unityMessageQueue.setProcessing(false);
    return;
  }

  queueCopy.forEach((queuedMessage, index) => {
    try {
      const [gameObject, method, message] = queuedMessage;

      const messageData = {
        type: "reactToUnity_command",
        gameObject,
        method,
        message: refreshEditorImportMessage(gameObject, method, message),
      };

      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage(messageData, "*");
      } else if (iframe.src) {
        // contentWindow가 없어도 src가 있으면 window.postMessage로 폴백
        window.postMessage(messageData, "*");
      } else {
        if (isDev) console.warn(`⚠️ 큐 메시지 ${index + 1} 처리 실패: Unity iframe 없음`);
        // 실패한 메시지는 다시 큐에 추가
        unityMessageQueue.enqueue(queuedMessage);
      }
    } catch (error) {
      console.error(`❌ 큐 메시지 ${index + 1} 처리 중 오류:`, error);
      // 실패한 메시지는 다시 큐에 추가
      unityMessageQueue.enqueue(queuedMessage);
    }
  });

  unityMessageQueue.setProcessing(false);
}

/** 고빈도 전송용: sendFn을 intervalMs(ms) 간격으로만 실행하도록 쓰로틀링 (THROTTLING.md 정책) */
export type SendToUnityWithPayloadFn = (
  command: string,
  payload: Record<string, unknown>
) => void | Promise<void>;

export type ThrottledSendToUnityFn = SendToUnityWithPayloadFn & {
  cancel: () => void;
  flush: () => void;
};

export function createThrottledSend(
  sendFn: SendToUnityWithPayloadFn,
  intervalMs: number
): ThrottledSendToUnityFn {
  let lastArgs: { command: string; payload: Record<string, unknown> } | null =
    null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastCall = 0;

  const flush = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    if (lastArgs) {
      const args = lastArgs;
      lastArgs = null;
      timer = null;
      sendFn(args.command, args.payload);
    }
  };

  const throttledSend = (command: string, payload: Record<string, unknown>) => {
    const now = Date.now();
    lastArgs = { command, payload };

    if (now - lastCall >= intervalMs) {
      lastCall = now;
      lastArgs = null;
      sendFn(command, payload);
      return;
    }

    if (timer == null) {
      timer = setTimeout(() => {
        lastCall = Date.now();
        flush();
      }, intervalMs - (now - lastCall));
    }
  };
  throttledSend.flush = flush;
  throttledSend.cancel = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    lastArgs = null;
  };
  return throttledSend;
}
